import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import {GetUsersApiResponse} from '@dms/api/shared';

import {Nullish} from 'shared/web';

/**
 * Unions a persisted (possibly now-inactive) assignee back into the active-users
 * list so an edit form keeps rendering the saved selection instead of silently
 * dropping it and forcing a reassignment on save.
 *
 * The extra single-user fetch fires only when the persisted id is missing from
 * the active list; otherwise it is skipped via `skipToken`.
 */
export function useUsersWithPersistedAssignee(
  activeUsers: GetUsersApiResponse | undefined,
  persistedUserId: string | Nullish
) {
  const missingUserId =
    isNotNilOrEmpty(persistedUserId) &&
    isNotNil(activeUsers) &&
    !activeUsers.some((user) => user.id === persistedUserId)
      ? persistedUserId
      : null;

  const {data: persistedUser} = useGetUserQuery(
    isNotNil(missingUserId) ? {id: missingUserId} : skipToken
  );

  return isNotNil(persistedUser) ? [...(activeUsers ?? []), persistedUser] : (activeUsers ?? []);
}
